import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_IDLE_AFTER_MS = 60_000;
const DEFAULT_INTERVAL_MS = 30_000;

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll'];

export function useHeartbeat(onHeartbeat, options) {
    const idleAfterMs = options?.idleAfterMs ?? DEFAULT_IDLE_AFTER_MS;
    const intervalMs = options?.intervalMs ?? DEFAULT_INTERVAL_MS;

    const [idle, setIdle] = useState(false);
    const lastActivityAt = useRef(null);
    const onHeartbeatRef = useRef(onHeartbeat);

    useEffect(() => {
        onHeartbeatRef.current = onHeartbeat;
    });

    const recordActivity = useCallback(() => {
        lastActivityAt.current = Date.now();
        setIdle(false);
    }, []);

    useEffect(() => {
        for (const evt of ACTIVITY_EVENTS) {
            window.addEventListener(evt, recordActivity, { passive: true });
        }
        document.addEventListener('visibilitychange', recordActivity);
        return () => {
            for (const evt of ACTIVITY_EVENTS) {
                window.removeEventListener(evt, recordActivity);
            }
            document.removeEventListener('visibilitychange', recordActivity);
        };
    }, [recordActivity]);

    useEffect(() => {
        lastActivityAt.current = Date.now();
    }, []);

    useEffect(() => {
        const timeout = setTimeout(onHeartbeatRef.current);
        const interval = setInterval(() => {
            if (document.hidden) return;
            const last = lastActivityAt.current;
            if (last !== null && Date.now() - last > idleAfterMs) {
                setIdle(true);
                return;
            }
            onHeartbeatRef.current();
        }, intervalMs);
        return () => {
            clearTimeout(timeout);
            clearInterval(interval);
        };
    }, [idleAfterMs, intervalMs]);

    const onIframeLoad = useCallback(e => {
        const doc = e.currentTarget.contentDocument;
        if (!doc) return;
        for (const evt of ACTIVITY_EVENTS) {
            doc.addEventListener(evt, recordActivity, { passive: true });
        }
    }, [recordActivity]);

    return { idle, onIframeLoad };
}
